/**
 * Route table — declarative react-router 7 convention (converged off the data
 * router / createBrowserRouter per the consolidation plan). Shape:
 *
 *   <Routes>
 *     <Route path="/signin" element={<SignInPage />} />          // public
 *     <Route element={<ProtectedRoute />}>                        // auth gate
 *       <Route path="/" element={<ShellLayout />}>                // TopBar + rail + <Outlet>
 *         {featureRoutes}                                         // modules append here
 *         ...inline expense + placeholders (pre-registry)...
 *         <Route path="auth/callback" element={<Navigate to="/" />} />
 *       </Route>
 *     </Route>
 *     <Route path="*" element={<Navigate to="/signin" />} />      // catch-all
 *   </Routes>
 *
 * The SHELL owns '/' (landing redirect → /crm/home, role-aware — see
 * LandingRedirect). One public
 * /signin; one ProtectedRoute wrapping the AppShell layout. /auth/callback lands
 * inside the protected tree — MSAL completes the Entra redirect in bootstrap()
 * and the gate routes the now-authenticated user to '/'.
 *
 * Module lifts (phases 2/4/5) move their routes into features/routes-registry.ts
 * (featureRoutes) instead of editing this file. The inline expense routes +
 * Chat/CRM placeholders below predate the registry and will migrate there.
 */
import { Placeholder } from '@andavo/web-ui';
import { Navigate, Route, Routes } from 'react-router';
import { AdminScreen } from '@/expense/AdminScreen';
import { ApprovalsScreen } from '@/expense/ApprovalsScreen';
import { BankScreen } from '@/expense/BankScreen';
import { ExpenseLayout } from '@/expense/ExpenseLayout';
import { MileageScreen } from '@/expense/MileageScreen';
import { ReceiptInboxScreen } from '@/expense/ReceiptInboxScreen';
import { ReportDetailScreen } from '@/expense/ReportDetailScreen';
import { ReportsScreen } from '@/expense/ReportsScreen';
import { featureRoutes } from '@/features/routes-registry';
import { ProtectedRoute } from '@/routing/ProtectedRoute';
import { ShellLayout } from '@/routing/ShellLayout';
import { LandingRedirect } from '@/routing/LandingRedirect';
import { SignInPage } from '@/routing/SignInPage';

export default function App() {
  return (
    <Routes>
      <Route path="/signin" element={<SignInPage />} />

      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<ShellLayout />}>
          {/* Lifted modules append their route lists here (chat /chat, admin
              /admin, crm /crm) — do NOT also declare inline routes for them. */}
          {featureRoutes}

          {/* Landing: Customer Management home. Was /expense/reports, which was
              only the landing because Expense was the one built module in phase 0.
              LandingRedirect prefers /crm/home but falls back to a page the user's
              role can actually open — see the note in LandingRedirect.tsx. */}
          <Route index element={<LandingRedirect />} />

          <Route path="expense" element={<ExpenseLayout />}>
            <Route index element={<Navigate replace to="/expense/reports" />} />
            <Route path="reports" element={<ReportsScreen />} />
            <Route path="reports/:id" element={<ReportDetailScreen />} />
            <Route path="receipts" element={<ReceiptInboxScreen />} />
            <Route path="banks" element={<BankScreen />} />
            <Route path="mileage" element={<MileageScreen />} />
            <Route path="approvals" element={<ApprovalsScreen />} />
            <Route path="admin" element={<AdminScreen />} />
          </Route>

          <Route path="settings" element={<Placeholder title="Settings" />} />
          <Route path="support" element={<Placeholder title="Support" />} />

          {/* MSAL completes the Entra redirect in bootstrap(); the callback URL
              lands here and the gate routes the authenticated user to '/'. */}
          <Route path="auth/callback" element={<Navigate replace to="/" />} />
        </Route>
      </Route>

      {/* Unknown paths → /signin (unauth) or, once authed, the gate's index. */}
      <Route path="*" element={<Navigate replace to="/" />} />
    </Routes>
  );
}
